import { useEffect, useRef, useState } from 'react';
import { ClipboardList, FileCheck, Boxes } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import useHasServiceStep from '@/hooks/useHasServiceStep';

interface OrderStepperProps {
  currentStep: 1 | 2 | 3;
  /**
   * The destination province of the order being built, when the caller knows
   * it. The service step can be skipped by a PROVINCE RULE, so the rail has to
   * be told the same destination the navigation used — otherwise it paints a
   * step for one city that the flow then skips for another.
   */
  provinceCode?: string | null;
  /**
   * The caller KNOWS this wizard has the service step, as a FACT rather than a
   * prediction — because it IS the step (SelectService), or because the
   * merchant demonstrably just came through it (the summary).
   *
   * A merchant whose rules already decided the service is never ROUTED through
   * the step, but reaches it via the summary's [Change] link. Predicting "no
   * such step" while they stand on it is the lying rail (QA D3); predicting it
   * on the summary AFTER they walked it erases the step behind them (QA D5).
   */
  serviceStepPresent?: boolean;
}

const DETAILS_STEP = { key: 'order_details', fallback: 'Order Details', icon: ClipboardList };
const SUMMARY_STEP = { key: 'summary', fallback: 'Summary', icon: FileCheck };

// SVC-STEP — what every merchant has INSTEAD of a carrier step. They do not
// choose who carries the parcel, they choose what the delivery promises.
const SELECT_SERVICE_STEP = {
  key: 'select_service_step',
  fallback: 'Select Service',
  icon: Boxes,
};

const STORAGE_KEY = 'order-stepper-prev-step';

const OrderStepper = ({ currentStep, provinceCode, serviceStepPresent }: OrderStepperProps) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';

  // Details -> Select Service -> Summary; no carrier step for any merchant
  // (owner 2026-10-28). QA SVC-F2 — the rail is told by the SAME source the
  // navigation uses (`useHasServiceStep`), so a skipped step is never drawn.
  const hasServiceStep = useHasServiceStep(provinceCode, serviceStepPresent);
  const steps = [DETAILS_STEP, ...(hasServiceStep ? [SELECT_SERVICE_STEP] : []), SUMMARY_STEP];

  // With no service step the wizard is two steps and the summary's
  // `currentStep={3}` folds to 2.
  const activeStep = !hasServiceStep && currentStep === 3 ? 2 : currentStep;
  const target = ((activeStep - 1) / (steps.length - 1)) * 100;

  const prevStep = useRef<number>(
    parseInt(sessionStorage.getItem(STORAGE_KEY) || String(activeStep), 10),
  );
  const initial = ((prevStep.current - 1) / (steps.length - 1)) * 100;
  const [progress, setProgress] = useState(Math.max(0, Math.min(100, initial)));

  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, String(activeStep));
    const timeout = setTimeout(() => setProgress(target), 80);
    return () => clearTimeout(timeout);
  }, [target, activeStep]);

  return (
    <div className='mb-6'>
      <div className='relative h-1.5 w-full rounded-full bg-sd-muted overflow-hidden'>
        <div
          className={`absolute inset-y-0 rounded-full bg-sd-primary transition-all duration-700 ease-out ${isRTL ? 'right-0' : 'left-0'}`}
          style={{ width: `${progress}%` }}
        />
      </div>
      <div className='flex justify-between mt-2.5'>
        {steps.map((step, i) => {
          const stepNum = i + 1;
          const Icon = step.icon;
          return (
            <span
              key={step.key}
              className={`flex items-center gap-1.5 text-xs font-medium transition-colors ${
                activeStep >= stepNum ? 'text-sd-foreground' : 'text-sd-muted-foreground'
              }`}
            >
              <Icon className='h-3.5 w-3.5' strokeWidth={1.5} />
              {String(t(step.key, step.fallback))}
            </span>
          );
        })}
      </div>
    </div>
  );
};

export default OrderStepper;
